import { create } from 'zustand'
import { devtools } from 'zustand/middleware'
import type { Conversation } from '@/lib/api/chat'

// Message part types - following mcp-chat pattern
export interface TextPart {
  type: 'text'
  text: string
}

export interface ToolInvocationPart {
  type: 'tool-invocation'
  toolInvocation: {
    toolCallId: string
    toolName: string
    state: 'call' | 'result'
    args?: unknown
    result?: unknown
  }
}

export interface MaxIterationsPart {
  type: 'max-iterations'
  maxIterations: {
    message: string
    iterations: number
    suggestion: string
  }
}

export interface ContextLengthPart {
  type: 'context-length'
  contextLength: {
    message: string
    suggestion: string
  }
}

export interface OutputTruncatedPart {
  type: 'output-truncated'
  outputTruncated: {
    message: string
    suggestion: string
  }
}

export type MessagePart =
  | TextPart
  | ToolInvocationPart
  | MaxIterationsPart
  | ContextLengthPart
  | OutputTruncatedPart

export interface Message {
  uuid: string
  conversation_uuid: string
  role: 'user' | 'assistant' | 'tool'
  content: string // Legacy: plain text content for backend compatibility
  parts?: MessagePart[] // New: structured content with tool invocations
  tool_calls?: Record<string, unknown>
  tool_call_id?: string
  tool_name?: string
  created_at: string
}

export interface ToolCallEvent {
  tool_call_id: string
  tool_name: string
  arguments?: string
  result?: string
  success?: boolean
  isExecuting: boolean
}

export interface MaxIterationsEvent {
  message: string
  iterations: number
  suggestion: string
}

export interface ContextLengthEvent {
  message: string
  suggestion: string
}

export interface OutputTruncatedEvent {
  message: string
  suggestion: string
}

export interface MessageContent {
  type: 'text' | 'tool_call' | 'max_iterations' | 'context_length' | 'output_truncated'
  content?: string
  toolCall?: ToolCallEvent
  maxIterations?: MaxIterationsEvent
  contextLength?: ContextLengthEvent
  outputTruncated?: OutputTruncatedEvent
}

interface ChatStore {
  // State
  conversations: Conversation[]
  currentConversationId: string | null
  messages: Message[]
  streamingContent: MessageContent[]
  isStreaming: boolean
  isLoadingConversations: boolean
  isLoadingMessages: boolean
  error: string | null
  failedMessageId: string | null

  // Actions
  pendingEnabledTools: string[] | null
  setPendingEnabledTools: (tools: string[] | null) => void

  setConversations: (conversations: Conversation[]) => void
  setCurrentConversationId: (id: string | null) => void
  setMessages: (messages: Message[]) => void
  addMessage: (message: Message) => void
  removeMessage: (uuid: string) => void
  addStreamingContent: (content: MessageContent) => void
  updateToolCall: (toolCallId: string, updates: Partial<ToolCallEvent>) => void
  clearStreamingContent: () => void
  setIsStreaming: (isStreaming: boolean) => void
  setIsLoadingConversations: (isLoading: boolean) => void
  setIsLoadingMessages: (isLoading: boolean) => void
  setError: (error: string | null) => void
  setFailedMessageId: (id: string | null) => void
  reset: () => void
}

const initialState = {
  conversations: [],
  currentConversationId: null,
  pendingEnabledTools: null,
  messages: [],
  streamingContent: [],
  isStreaming: false,
  isLoadingConversations: false,
  isLoadingMessages: false,
  error: null,
  failedMessageId: null,
}

export const useChatStore = create<ChatStore>()(
  devtools(
    (set) => ({
      ...initialState,

      setConversations: (conversations) => set({ conversations }),

      setCurrentConversationId: (id) => set({ currentConversationId: id }),

      setPendingEnabledTools: (pendingEnabledTools) => set({ pendingEnabledTools }),

      setMessages: (messages) => set({ messages }),

      addMessage: (message) => {
        set((state) => ({
          messages: [...state.messages, message],
        }))
      },

      removeMessage: (uuid) => {
        set((state) => ({
          messages: state.messages.filter((m) => m.uuid !== uuid),
        }))
      },

      addStreamingContent: (content) =>
        set((state) => {
          // Special handling for text content - always keep only one text block at the start
          if (content.type === 'text') {
            // Find if there's already a text content
            const textIndex = state.streamingContent.findIndex((item) => item.type === 'text')
            if (textIndex >= 0) {
              // Replace existing text content
              const newContent = [...state.streamingContent]
              newContent[textIndex] = content
              return { streamingContent: newContent }
            } else {
              // Add new text content at the beginning
              return { streamingContent: [content, ...state.streamingContent] }
            }
          } else if (content.type === 'tool_call' && content.toolCall) {
            // For tool calls, check if it already exists
            const existingIndex = state.streamingContent.findIndex(
              (item) =>
                item.type === 'tool_call' &&
                item.toolCall?.tool_call_id === content.toolCall?.tool_call_id,
            )

            if (existingIndex >= 0) {
              // Update existing tool call
              const newContent = [...state.streamingContent]
              newContent[existingIndex] = content
              return { streamingContent: newContent }
            } else {
              // Add new tool call
              return { streamingContent: [...state.streamingContent, content] }
            }
          } else if (content.type === 'max_iterations') {
            // Add max iterations warning at the end
            return { streamingContent: [...state.streamingContent, content] }
          } else if (content.type === 'context_length') {
            // Add context length exceeded warning at the end
            return { streamingContent: [...state.streamingContent, content] }
          } else if (content.type === 'output_truncated') {
            // Add output truncated warning at the end
            return { streamingContent: [...state.streamingContent, content] }
          } else {
            // Default: just append
            return { streamingContent: [...state.streamingContent, content] }
          }
        }),

      updateToolCall: (toolCallId, updates) =>
        set((state) => ({
          streamingContent: state.streamingContent.map((item) =>
            item.type === 'tool_call' && item.toolCall?.tool_call_id === toolCallId
              ? { ...item, toolCall: { ...item.toolCall!, ...updates } }
              : item,
          ),
        })),

      clearStreamingContent: () => set({ streamingContent: [] }),

      setIsStreaming: (isStreaming) => set({ isStreaming }),

      setIsLoadingConversations: (isLoading) => set({ isLoadingConversations: isLoading }),

      setIsLoadingMessages: (isLoading) => set({ isLoadingMessages: isLoading }),

      setError: (error) => set({ error }),

      setFailedMessageId: (id) => set({ failedMessageId: id }),

      reset: () => set(initialState),
    }),
    { name: 'chat-store' },
  ),
)
